<script setup lang="ts">
import { computed, ref, type CSSProperties } from "vue";

type Location =
  | "top" | "bottom" | "start" | "end"
  | "top start" | "top end" | "bottom start" | "bottom end"
  | "left" | "right" | "center";

type PopupMode = "contain" | "scroll";

const props = withDefaults(defineProps<{
  src: string;
  popupSrc?: string;
  alt?: string;

  thumbWidth?: string | number;
  thumbHeight?: string | number;
  thumbCover?: boolean;
  thumbRound?: boolean;

  showHint?: boolean;
  hintText?: string;

  popupWidth?: string;
  popupHeight?: string;
  popupAspectRatio?: number;

  popupMaxWidth?: string;
  popupMaxHeight?: string;

  location?: Location;
  locationStrategy?: "connected" | "static";
  offset?: number | string;

  scrim?: boolean | string;
  openOnHover?: boolean;
  closeOnContentClick?: boolean;
  openDelay?: number | string;
  closeDelay?: number | string;
  closeOnEsc?: boolean;

  popupMode?: PopupMode;
}>(), {
  popupSrc: undefined,
  alt: "",

  thumbWidth: undefined,
  thumbHeight: 150,
  thumbCover: true,
  thumbRound: false,

  showHint: true,
  hintText: "vai sull'immagine per ingrandire",

  popupWidth: "50vw",
  popupHeight: "min(90vh, 50vw)",
  popupAspectRatio: undefined,

  popupMaxWidth: "90vw",
  popupMaxHeight: "90vh",

  location: "center",
  locationStrategy: "static",
  offset: 8,

  scrim: true,
  openOnHover: true,
  closeOnContentClick: false,
  openDelay: 80,
  closeDelay: 120,
  closeOnEsc: true,

  popupMode: "contain",
});

/* === Thumbnail sizing === */
const thumbSize = computed<string | number>(() => {
  if (props.thumbRound) {
    if (props.thumbWidth !== undefined) return props.thumbWidth;
    if (props.thumbHeight !== undefined) return props.thumbHeight;
    return 150;
  }
  return 0;
});

const thumbImgStyle = computed<CSSProperties>(() => ({
  display: "block",
  width: "100%",
  height: "100%",
  objectFit: props.thumbCover ? "cover" : "contain",
}));

const thumbRectBoxStyle = computed<CSSProperties>(() => {
  const s: CSSProperties = {
    display: "inline-block",
    overflow: "hidden",
    borderRadius: "12px",
  };
  if (props.thumbWidth !== undefined) {
    s.width = typeof props.thumbWidth === "number" ? `${props.thumbWidth}px` : props.thumbWidth;
  }
  if (props.thumbHeight !== undefined) {
    s.height = typeof props.thumbHeight === "number" ? `${props.thumbHeight}px` : props.thumbHeight;
  } else if (!props.thumbWidth) {
    s.maxWidth = "100%";
  }
  return s;
});

/* === Popup sizing === */
const popupError = ref(false);
function onPopupError() { popupError.value = true; }

const popupBoxStyle = computed<CSSProperties>(() => {
  if (props.popupAspectRatio) {
    return {
      width: props.popupWidth,
      maxWidth: props.popupMaxWidth,
      maxHeight: props.popupMaxHeight,
      aspectRatio: String(props.popupAspectRatio),
      overflow: props.popupMode === "scroll" ? "auto" : "hidden",
    };
  }
  return {
    width: props.popupWidth,
    maxWidth: props.popupMaxWidth,
    height: props.popupHeight,
    maxHeight: props.popupMaxHeight,
    overflow: props.popupMode === "scroll" ? "auto" : "hidden",
  };
});

/* ➕ classe per gestire centratura diversa per 'contain' vs 'scroll' */
const popupModeClass = computed(() =>
  props.popupMode === "scroll" ? "ih-mode-scroll" : "ih-mode-contain"
);
</script>

<template>
  <v-menu
    :open-on-hover="openOnHover"
    :close-on-content-click="closeOnContentClick"
    :location="location"
    :location-strategy="locationStrategy"
    :offset="offset"
    :scrim="scrim"
    :open-delay="openDelay"
    :close-delay="closeDelay"
    :close-on-esc="closeOnEsc"
  >
    <template #activator="{ props: act }">
      <div class="ih-activator-wrap">
        <div
          v-bind="act"
          class="ih-activator"
          tabindex="0"
          :aria-label="alt || 'Anteprima immagine'"
        >
          <v-avatar v-if="thumbRound" :size="thumbSize" class="ih-avatar">
            <img :src="src" :alt="alt" :style="thumbImgStyle" />
          </v-avatar>

          <div v-else class="ih-rect" :style="thumbRectBoxStyle">
            <img :src="src" :alt="alt" :style="thumbImgStyle" />
          </div>
        </div>

        <div v-if="showHint" class="ih-hint">{{ hintText }}</div>
      </div>
    </template>

    <v-card elevation="8" class="pa-2 ih-popup-card">
      <!-- ➕ aggiungo la classe in base alla modalità -->
      <div class="ih-popup-box" :class="popupModeClass" :style="popupBoxStyle">
        <!-- MODE: CONTAIN -->
        <v-img
          v-if="popupMode === 'contain' && !popupError"
          :src="popupSrc || src"
          :alt="alt"
          eager
          width="100%"
          height="100%"
          class="ih-img-contain"
          @error="onPopupError"
        >
          <template #placeholder>
            <div class="ih-placeholder">Caricamento…</div>
          </template>
        </v-img>

        <!-- MODE: SCROLL -->
        <img
          v-else-if="popupMode === 'scroll' && !popupError"
          :src="popupSrc || src"
          :alt="alt"
          class="ih-img-scroll"
        />

        <!-- Fallback -->
        <img v-else :src="popupSrc || src" :alt="alt" class="ih-img-contain-fallback" />
      </div>
    </v-card>
  </v-menu>
</template>

<style scoped>
/* Attivatore + hint */
.ih-activator-wrap {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}
.ih-activator {
  cursor: zoom-in;
}
.ih-avatar {
  overflow: hidden;
}
.ih-rect {
  overflow: hidden;
  border-radius: 12px;
}

.ih-hint {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  opacity: 0.7;
  user-select: none;
}

/* Popup container */
.ih-popup-card {
  border-radius: 12px;
}
.ih-popup-box {
  border-radius: 8px;
}

/* ➕ Centratura per CONTAIN: usa grid per centrare v-img dentro il box */
.ih-popup-box.ih-mode-contain {
  display: grid;
  place-items: center; /* centra orizzontale + verticale */
}

/* v-img interno in CONTAIN: fit e centratura */
.ih-img-contain :deep(.v-img__img) {
  object-fit: contain !important;
  object-position: center center !important; /* <-- centratura esplicita */
  width: 100%;
  height: 100%;
  display: block;
}

/* Fallback contain (img nativo) */
.ih-img-contain-fallback {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
}

/* ➕ Centratura per SCROLL:
   - text-align:center per centrare immagini più strette
   - l'immagine può superare il box e si scorre */
.ih-popup-box.ih-mode-scroll {
  overflow: auto; /* già gestito anche inline, qui per chiarezza */
  text-align: center; /* centra inline-block orizzontalmente */
}

/* img in modalità scroll:
   - non forziamo width 100% (evita upscaling), ma centriamo con inline-block
   - se è più larga del box, comparirà lo scrollbar orizzontale (se vuoi evitarlo: max-width:100%) */
.ih-img-scroll {
  display: block;
  height: auto;
  max-width: 100%; /*  usa max-width:100% se vuoi evitare scroll orizzontale */
}
</style>
